import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  resolveColumns,
  tablePreferenceSchema,
  tablePreferencesSchema,
  type SaveTablePreferenceInput,
  type TableKey,
  type TablePreference,
  type TablePreferences,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';

/**
 * Which columns this person sees, per list.
 *
 * All four lists arrive in ONE response and are cached hard, because a preference changes when
 * somebody deliberately changes it and at no other time — and every screen that renders a list
 * would otherwise ask for its own on every visit.
 *
 * `useTableColumns` never returns `undefined`: while the preference is loading it answers with
 * the registry's own defaults, resolved by the same shared function the server uses. A list that
 * rendered nothing until its column preference arrived would flash empty on every navigation to
 * spare a preference almost nobody has set.
 */

const PREFERENCES = '/settings/table-preferences';

export const tablePreferenceKeys = {
  all: ['table-preferences'] as const,
};

export function useTablePreferences(): UseQueryResult<TablePreferences> {
  const client = useApiClient();
  return useQuery({
    queryKey: tablePreferenceKeys.all,
    queryFn: () => client.get(PREFERENCES, { schema: tablePreferencesSchema }),
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/** The columns one list shows, defaults included. Never `undefined`, ever. */
export function useTableColumns(table: TableKey): string[] {
  const { data } = useTablePreferences();
  const stored = data?.tables.find((one) => one.table === table);
  return stored ? stored.columns : resolveColumns(table, null);
}

export function useSaveTableColumns(): UseMutationResult<
  TablePreference,
  unknown,
  SaveTablePreferenceInput
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SaveTablePreferenceInput) =>
      client.put(PREFERENCES, input, { schema: tablePreferenceSchema }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tablePreferenceKeys.all });
    },
  });
}

export function useResetTableColumns(): UseMutationResult<TablePreference, unknown, TableKey> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (table: TableKey) =>
      client.del(`${PREFERENCES}/${table}`, undefined, { schema: tablePreferenceSchema }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tablePreferenceKeys.all });
    },
  });
}
